import React, { useState } from 'react';
import { useTheme } from '../App';
import { X, Printer, Scan, Edit, Trash2, AlertTriangle, ChevronDown, ChevronRight, Check, XCircle, Clock } from 'lucide-react';
interface ReviewModalProps {
  action: any;
  onClose: () => void;
}
interface Batch {
  id: string;
  batchName: string;
  productName: string;
  category: string;
  sellType: string;
  missingFields: string[];
  status: 'pending' | 'updated' | 'ignored';
  fieldValues: {
    [key: string]: string;
  };
}
// Helper function to format currency
const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};
const ReviewModal: React.FC<ReviewModalProps> = ({
  action,
  onClose
}) => {
  const {
    isDarkMode
  } = useTheme();
  const [assignee, setAssignee] = useState('');
  const [showSlideout, setShowSlideout] = useState(false);
  const [expandedBatchId, setExpandedBatchId] = useState<string | null>(null);
  // Sample batch data based on the CSV
  const [batches, setBatches] = useState<Batch[]>([{
    id: '1',
    batchName: 'CR25-CH37',
    productName: 'Crave - Crime Scene Bar 100mg',
    category: 'Chocolates',
    sellType: 'ALL',
    missingFields: ['Product Flower Type'],
    status: 'pending',
    fieldValues: {}
  }, {
    id: '2',
    batchName: 'CR25-CH25',
    productName: 'Crave - Mini Coco Crunch Chocolate 10mg',
    category: 'Chocolates',
    sellType: 'ALL',
    missingFields: ['Product Flower Type'],
    status: 'pending',
    fieldValues: {}
  }, {
    id: '3',
    batchName: '589F9-1918-G3125',
    productName: 'Black Label - Warheads 3.5g',
    category: 'Flower (Hybrid)',
    sellType: 'ALL',
    missingFields: ['Product Flower Type'],
    status: 'pending',
    fieldValues: {}
  }, {
    id: '4',
    batchName: '25174SRL01',
    productName: 'Camino - Raspberry Lemonade Sours 100mg',
    category: 'Gummies',
    sellType: 'ALL',
    missingFields: ['Product Flower Type'],
    status: 'pending',
    fieldValues: {}
  }, {
    id: '5',
    batchName: 'DZ25-D007',
    productName: 'Dunzo - Blueberry Dreams Disposable 1g',
    category: 'Vape',
    sellType: 'Both',
    missingFields: ['Product Flower Type'],
    status: 'pending',
    fieldValues: {}
  }, {
    id: '6',
    batchName: 'JJLD-PINLEM-061925',
    productName: 'Jeeter - Pink Lemon Liquid Diamond Vape 1g',
    category: 'Vape',
    sellType: 'Both',
    missingFields: ['Product Flower Type'],
    status: 'pending',
    fieldValues: {}
  }, {
    id: '7',
    batchName: 'TLG107PPGT',
    productName: 'Plug Play - DNA Ghost Train 1g',
    category: 'Vape',
    sellType: 'Both',
    missingFields: ['Product Flower Type'],
    status: 'pending',
    fieldValues: {}
  }, {
    id: '8',
    batchName: 'VPPL235223',
    productName: 'West Coast Cure - Blackberry Kush CUREpen 1g',
    category: 'Vape',
    sellType: 'Both',
    missingFields: ['Product Flower Type'],
    status: 'pending',
    fieldValues: {}
  }]);
  const handleFieldChange = (batchId: string, fieldName: string, value: string) => {
    setBatches(batches.map(batch => batch.id === batchId ? {
      ...batch,
      fieldValues: {
        ...batch.fieldValues,
        [fieldName]: value
      }
    } : batch));
  };
  const handleSaveBatch = (batchId: string) => {
    setBatches(batches.map(batch => batch.id === batchId ? {
      ...batch,
      status: 'updated'
    } : batch));
    setExpandedBatchId(null);
  };
  const handleIgnoreBatch = (batchId: string) => {
    setBatches(batches.map(batch => batch.id === batchId ? {
      ...batch,
      status: 'ignored'
    } : batch));
    setExpandedBatchId(null);
  };
  const toggleBatch = (batchId: string) => {
    setExpandedBatchId(expandedBatchId === batchId ? null : batchId);
  };
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'updated':
        return <Check size={16} className="text-green-500" data-id="element-313" />;
      case 'ignored':
        return <XCircle size={16} className="text-gray-500" data-id="element-314" />;
      case 'pending':
        return <Clock size={16} className="text-yellow-500" data-id="element-315" />;
      default:
        return null;
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'updated':
        return 'border-l-green-500';
      case 'ignored':
        return 'border-l-gray-500';
      case 'pending':
        return 'border-l-yellow-500';
      default:
        return 'border-l-gray-700';
    }
  };
  // Simulate data for the modal
  const data = {
    type: action.title === 'Batch Alert / Missing Data' ? 'batchAlert' : 'default',
    products: [{
      id: '1',
      product: 'Blue Dream 3.5g',
      skuBirth: '2023-05-15',
      lastBatchAdded: '2023-06-20',
      dailyVelocity: 0.3,
      daysRemaining: 42,
      unitsRemaining: 15,
      currentPrice: 45.0,
      proposedPrice: 39.99,
      margin: 32,
      isSlowMover: true,
      promote: false,
      exclude: false,
      batchExpiration: '2023-09-30',
      sellThroughRate: '0.8 units/day',
      estimatedStockoutDate: '2023-10-15',
      safeQty: 10,
      fohQty: 2,
      minsRemaining: 120,
      batch: 'BD-202305',
      suggestedQty: 3,
      wholesaleCost: 30.0,
      totalCost: 300.0,
      qty: 10,
      blazeQty: 25,
      complianceQty: 23,
      difference: -2,
      age: 45,
      velocity: 0.3,
      remaining: 15,
      marginPercent: 32
    }, {
      id: '2',
      product: 'Sour Diesel 1g',
      skuBirth: '2023-06-01',
      lastBatchAdded: '2023-07-05',
      dailyVelocity: 0.5,
      daysRemaining: 38,
      unitsRemaining: 19,
      currentPrice: 15.0,
      proposedPrice: 12.99,
      margin: 28,
      isSlowMover: true,
      promote: true,
      exclude: false,
      batchExpiration: '2023-10-15',
      sellThroughRate: '1.2 units/day',
      estimatedStockoutDate: '2023-09-30',
      safeQty: 15,
      fohQty: 3,
      minsRemaining: 90,
      batch: 'SD-202306',
      suggestedQty: 5,
      wholesaleCost: 10.0,
      totalCost: 150.0,
      qty: 15,
      blazeQty: 30,
      complianceQty: 32,
      difference: 2,
      age: 38,
      velocity: 0.5,
      remaining: 19,
      marginPercent: 28
    }],
    transactions: [{
      id: '1',
      dateTime: '2023-08-15 14:30',
      transactionNumber: 'TX-45678',
      amount: 125.45
    }, {
      id: '2',
      dateTime: '2023-08-15 16:15',
      transactionNumber: 'TX-45679',
      amount: 89.99
    }, {
      id: '3',
      dateTime: '2023-08-15 17:45',
      transactionNumber: 'TX-45680',
      amount: 210.32
    }],
    vendor: 'Green Farms Wholesale',
    terms: 'Net 30',
    targetMargin: 35
  };
  // Render different content based on action type
  const renderModalContent = () => {
    if (data.type === 'batchAlert') {
      return <div className="mb-6" data-id="element-316">
          <h3 className="text-lg font-semibold mb-3" data-id="element-317">Impacted Batches</h3>
          <div className={`overflow-x-auto rounded-lg border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-318">
            <table className="w-full" data-id="element-319">
              <thead className={isDarkMode ? 'bg-[#161c22]' : 'bg-gray-50'} data-id="element-320">
                <tr data-id="element-321">
                  <th className="px-4 py-3 text-left text-sm font-medium" data-id="element-322">
                    Batch Name
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium" data-id="element-323">
                    Product Name
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium" data-id="element-324">
                    Category
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium" data-id="element-325">
                    Sell Type
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium" data-id="element-326">
                    Missing Field(s)
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200" data-id="element-327">
                {batches.map(batch => <tr key={batch.id} className={isDarkMode ? 'hover:bg-[#161c22]' : 'hover:bg-gray-50'} data-id="element-328">
                    <td className="px-4 py-3 text-sm" data-id="element-329">{batch.batchName}</td>
                    <td className="px-4 py-3 text-sm" data-id="element-330">{batch.productName}</td>
                    <td className="px-4 py-3 text-sm" data-id="element-331">{batch.category}</td>
                    <td className="px-4 py-3 text-sm" data-id="element-332">{batch.sellType}</td>
                    <td className="px-4 py-3 text-sm" data-id="element-333">
                      {batch.missingFields.join(', ')}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>;
    }
    // Default content for other action types
    switch (data.type) {
      case 'slowMovers':
        return <div className="mb-6" data-id="element-334">
            <h3 className="text-lg font-semibold mb-3" data-id="element-335">Slow Moving Products</h3>
            <div className={`overflow-x-auto rounded-lg border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-336">
              <table className="w-full" data-id="element-337">
                <thead className={isDarkMode ? 'bg-[#161c22]' : 'bg-gray-50'} data-id="element-338">
                  <tr data-id="element-339">
                    <th className="px-4 py-3 text-left text-sm font-medium" data-id="element-340">
                      Product
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium" data-id="element-341">
                      SKU Birth
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium" data-id="element-342">
                      Last Batch Added
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium" data-id="element-343">
                      Daily Velocity
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium" data-id="element-344">
                      Days Remaining
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium" data-id="element-345">
                      Units Remaining
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium" data-id="element-346">
                      Current Price
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium" data-id="element-347">
                      Proposed Price
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium" data-id="element-348">
                      Margin %
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium" data-id="element-349">
                      Slow Mover
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium" data-id="element-350">
                      Promote
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium" data-id="element-351">
                      Exclude
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-352">
                  {data.products.map(item => <tr key={item.id} className={isDarkMode ? 'hover:bg-[#161c22]' : 'hover:bg-gray-50'} data-id="element-353">
                      <td className="px-4 py-3 text-sm" data-id="element-354">{item.product}</td>
                      <td className="px-4 py-3 text-sm" data-id="element-355">{item.skuBirth}</td>
                      <td className="px-4 py-3 text-sm" data-id="element-356">
                        {item.lastBatchAdded}
                      </td>
                      <td className="px-4 py-3 text-sm" data-id="element-357">
                        {item.dailyVelocity}
                      </td>
                      <td className="px-4 py-3 text-sm" data-id="element-358">
                        {item.daysRemaining}
                      </td>
                      <td className="px-4 py-3 text-sm" data-id="element-359">
                        {item.unitsRemaining}
                      </td>
                      <td className="px-4 py-3 text-sm" data-id="element-360">
                        {formatCurrency(item.currentPrice)}
                      </td>
                      <td className="px-4 py-3 text-sm" data-id="element-361">
                        <input type="number" step="0.01" defaultValue={item.proposedPrice} className={`w-24 px-2 py-1 rounded border ${isDarkMode ? 'bg-[#161c22] border-gray-700' : 'bg-white border-gray-300'}`} data-id="element-362" />
                      </td>
                      <td className="px-4 py-3 text-sm" data-id="element-363">{item.margin}%</td>
                      <td className="px-4 py-3 text-sm" data-id="element-364">
                        <input type="checkbox" defaultChecked={item.isSlowMover} disabled className="rounded" data-id="element-365" />
                      </td>
                      <td className="px-4 py-3 text-sm" data-id="element-366">
                        <input type="checkbox" defaultChecked={item.promote} className="rounded" data-id="element-367" />
                      </td>
                      <td className="px-4 py-3 text-sm" data-id="element-368">
                        <input type="checkbox" defaultChecked={item.exclude} className="rounded" data-id="element-369" />
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>;
      default:
        return <div className="mb-6" data-id="element-370">
            <h3 className="text-lg font-semibold mb-3" data-id="element-371">
              Products to Take Action On
            </h3>
            <div className={`overflow-x-auto rounded-lg border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-372">
              <table className="w-full" data-id="element-373">
                <thead className={isDarkMode ? 'bg-[#161c22]' : 'bg-gray-50'} data-id="element-374">
                  <tr data-id="element-375">
                    <th className="px-4 py-3 text-left text-sm font-medium" data-id="element-376">
                      Product
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium" data-id="element-377">
                      Age (days)
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium" data-id="element-378">
                      Daily Velocity
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium" data-id="element-379">
                      Units Remaining
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium" data-id="element-380">
                      Current Price
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium" data-id="element-381">
                      Proposed Price
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium" data-id="element-382">
                      Margin %
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium" data-id="element-383">
                      Promote
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-384">
                  {data.products.map(item => <tr key={item.id} className={isDarkMode ? 'hover:bg-[#161c22]' : 'hover:bg-gray-50'} data-id="element-385">
                      <td className="px-4 py-3 text-sm" data-id="element-386">{item.product}</td>
                      <td className="px-4 py-3 text-sm" data-id="element-387">{item.age}</td>
                      <td className="px-4 py-3 text-sm" data-id="element-388">{item.velocity}</td>
                      <td className="px-4 py-3 text-sm" data-id="element-389">{item.remaining}</td>
                      <td className="px-4 py-3 text-sm" data-id="element-390">
                        {formatCurrency(item.currentPrice)}
                      </td>
                      <td className="px-4 py-3 text-sm" data-id="element-391">
                        <input type="number" step="0.01" defaultValue={item.proposedPrice} className={`w-24 px-2 py-1 rounded border ${isDarkMode ? 'bg-[#161c22] border-gray-700' : 'bg-white border-gray-300'}`} data-id="element-392" />
                      </td>
                      <td className="px-4 py-3 text-sm" data-id="element-393">{item.margin}%</td>
                      <td className="px-4 py-3 text-sm" data-id="element-394">
                        <input type="checkbox" className="rounded" data-id="element-395" />
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>;
    }
  };
  const handleRunPlay = () => {
    if (data.type === 'batchAlert') {
      setShowSlideout(true);
    } else {
      // Default behavior for other action types
      console.log('Running play...');
      onClose();
    }
  };
  return <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={onClose} data-id="element-396">
        <div className={`relative w-11/12 max-w-6xl max-h-[90vh] overflow-auto rounded-lg shadow-lg ${isDarkMode ? 'bg-[#222930] text-white' : 'bg-white text-black'}`} onClick={e => e.stopPropagation()} data-id="element-397">
          <div className="sticky top-0 z-10 flex items-center justify-between p-4 border-b border-gray-700" data-id="element-398">
            <div data-id="element-399">
              <h2 className="text-xl font-bold" data-id="element-400">{action.title}</h2>
              <p className="text-sm text-gray-400" data-id="element-401">{action.description}</p>
            </div>
            <button className="p-1 rounded-full hover:bg-gray-700" onClick={onClose} data-id="element-402">
              <X size={24} data-id="element-403" />
            </button>
          </div>
          <div className="p-4" data-id="element-404">
            {/* Stats section */}
            <div className="grid grid-cols-4 gap-4 mb-6" data-id="element-405">
              <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-[#161c22]' : 'bg-gray-50'}`} data-id="element-406">
                <p className="text-sm text-gray-400" data-id="element-407">Confidence</p>
                <p className="text-xl font-bold text-[#2196f3]" data-id="element-408">
                  {action.confidenceScore}%
                </p>
              </div>
              <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-[#161c22]' : 'bg-gray-50'}`} data-id="element-409">
                <p className="text-sm text-gray-400" data-id="element-410">{action.valueType}</p>
                <p className="text-xl font-bold text-green-500" data-id="element-411">
                  {formatCurrency(action.valueAmount)}
                </p>
              </div>
              <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-[#161c22]' : 'bg-gray-50'}`} data-id="element-412">
                <p className="text-sm text-gray-400" data-id="element-413">Time Saved</p>
                <p className="text-xl font-bold text-purple-500" data-id="element-414">
                  {action.timeSaved} mins
                </p>
              </div>
              <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-[#161c22]' : 'bg-gray-50'}`} data-id="element-415">
                <p className="text-sm text-gray-400" data-id="element-416">Size</p>
                <p className="text-xl font-bold" data-id="element-417">
                  {action.size} (
                  {action.size === 'S' ? 3 : action.size === 'M' ? 6 : 10}{' '}
                  credits)
                </p>
              </div>
            </div>
            {/* Main content */}
            {renderModalContent()}
            {/* Assignee section */}
            {data.type !== 'batchAlert' && <div className="mb-6" data-id="element-418">
                <label className="block text-sm font-medium mb-2" data-id="element-419">
                  Assign to Team Member (optional)
                </label>
                <select value={assignee} onChange={e => setAssignee(e.target.value)} className={`w-full p-2 rounded-md border ${isDarkMode ? 'bg-[#161c22] border-gray-700' : 'bg-white border-gray-300'}`} data-id="element-420">
                  <option value="" data-id="element-421">Select team member</option>
                  <option value="john" data-id="element-422">John Doe</option>
                  <option value="jane" data-id="element-423">Jane Smith</option>
                  <option value="mike" data-id="element-424">Mike Johnson</option>
                </select>
              </div>}
            {/* Action buttons */}
            <div className="flex justify-end space-x-3" data-id="element-425">
              <button className={`px-4 py-2 rounded-md ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`} onClick={onClose} data-id="element-426">
                Cancel
              </button>
              <button className="px-4 py-2 bg-[#2196f3] text-white rounded-md hover:bg-[#1976d2]" onClick={handleRunPlay} data-id="element-427">
                Run This Play
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Slideout Panel */}
      {showSlideout && <div className="fixed inset-0 z-[60] bg-black bg-opacity-50" onClick={() => setShowSlideout(false)} data-id="element-428">
          <div className={`fixed right-0 top-0 h-full w-full max-w-2xl overflow-auto shadow-xl ${isDarkMode ? 'bg-[#222930] text-white' : 'bg-white text-black'}`} onClick={e => e.stopPropagation()} data-id="element-429">
            {/* Slideout Header */}
            <div className="sticky top-0 z-10 flex items-center justify-between p-4 border-b border-gray-700" data-id="element-430">
              <div data-id="element-431">
                <h2 className="text-xl font-bold" data-id="element-432">Update Batch Data</h2>
                <p className="text-sm text-gray-400" data-id="element-433">
                  {batches.filter(b => b.status === 'updated').length} of{' '}
                  {batches.length} batches updated
                </p>
              </div>
              <button className="p-1 rounded-full hover:bg-gray-700" onClick={() => setShowSlideout(false)} data-id="element-434">
                <X size={24} data-id="element-435" />
              </button>
            </div>
            {/* Batch List */}
            <div className="p-4 space-y-3" data-id="element-436">
              {batches.map(batch => <div key={batch.id} className={`border-l-4 ${getStatusColor(batch.status)} rounded-lg overflow-hidden ${isDarkMode ? 'bg-[#161c22] border-gray-700' : 'bg-gray-50 border-gray-200'}`} data-id="element-437">
                  {/* Collapsed Header */}
                  <div className="p-4 cursor-pointer hover:bg-opacity-80" onClick={() => toggleBatch(batch.id)} data-id="element-438">
                    <div className="flex items-center justify-between" data-id="element-439">
                      <div className="flex items-center space-x-3 flex-1" data-id="element-440">
                        {expandedBatchId === batch.id ? <ChevronDown size={20} className="text-gray-400" data-id="element-441" /> : <ChevronRight size={20} className="text-gray-400" data-id="element-442" />}
                        <div className="flex-1" data-id="element-443">
                          <div className="flex items-center space-x-2" data-id="element-444">
                            <h3 className="font-semibold" data-id="element-445">{batch.batchName}</h3>
                            {getStatusIcon(batch.status)}
                          </div>
                          <p className="text-sm text-gray-400" data-id="element-446">
                            {batch.productName}
                          </p>
                        </div>
                      </div>
                      {expandedBatchId !== batch.id && batch.status === 'pending' && <div className="flex space-x-2" data-id="element-447">
                            <button className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700" onClick={e => {
                    e.stopPropagation();
                    handleIgnoreBatch(batch.id);
                  }} data-id="element-448">
                              Ignore
                            </button>
                          </div>}
                    </div>
                  </div>
                  {/* Expanded Content */}
                  {expandedBatchId === batch.id && <div className="px-4 pb-4 border-t border-gray-700" data-id="element-449">
                      <div className="mt-4 space-y-4" data-id="element-450">
                        {/* Batch Details */}
                        <div className="grid grid-cols-2 gap-4" data-id="element-451">
                          <div data-id="element-452">
                            <p className="text-sm text-gray-400" data-id="element-453">Batch Name</p>
                            <p className="font-medium" data-id="element-454">{batch.batchName}</p>
                          </div>
                          <div data-id="element-455">
                            <p className="text-sm text-gray-400" data-id="element-456">
                              Product Name
                            </p>
                            <p className="font-medium" data-id="element-457">{batch.productName}</p>
                          </div>
                          <div data-id="element-458">
                            <p className="text-sm text-gray-400" data-id="element-459">Category</p>
                            <p className="font-medium" data-id="element-460">{batch.category}</p>
                          </div>
                          <div data-id="element-461">
                            <p className="text-sm text-gray-400" data-id="element-462">Sell Type</p>
                            <p className="font-medium" data-id="element-463">{batch.sellType}</p>
                          </div>
                        </div>
                        {/* Missing Fields */}
                        <div className="space-y-3 mt-4" data-id="element-464">
                          <h4 className="font-semibold" data-id="element-465">Missing Fields</h4>
                          {batch.missingFields.map(field => <div key={field} data-id="element-466">
                              <label className="block text-sm text-gray-400 mb-1" data-id="element-467">
                                {field}
                              </label>
                              <input type="text" placeholder={`Enter ${field}`} value={batch.fieldValues[field] || ''} onChange={e => handleFieldChange(batch.id, field, e.target.value)} className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-[#0d1117] border-gray-700' : 'bg-white border-gray-300'}`} data-id="element-468" />
                            </div>)}
                        </div>
                        {/* Action Buttons */}
                        <div className="flex justify-end space-x-3 mt-4 pt-4 border-t border-gray-700" data-id="element-469">
                          <button className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700" onClick={() => handleIgnoreBatch(batch.id)} data-id="element-470">
                            Ignore
                          </button>
                          <button className="px-4 py-2 bg-[#2196f3] text-white rounded hover:bg-[#1976d2]" onClick={() => handleSaveBatch(batch.id)} data-id="element-471">
                            Save
                          </button>
                        </div>
                      </div>
                    </div>}
                </div>)}
            </div>
            {/* Footer Actions */}
            <div className="sticky bottom-0 p-4 border-t border-gray-700 bg-opacity-95" data-id="element-472">
              <div className="flex justify-between items-center" data-id="element-473">
                <button className={`px-4 py-2 rounded ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`} onClick={() => setShowSlideout(false)} data-id="element-474">
                  Save Progress & Close
                </button>
                <button className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700" onClick={() => {
              console.log('Completing play...');
              setShowSlideout(false);
              onClose();
            }} data-id="element-475">
                  Complete Play
                </button>
              </div>
            </div>
          </div>
        </div>}
    </>;
};
export default ReviewModal;